import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { deleteCustomerUserApi, getCustomerUsersApi } from 'api/adminApi';
import DataTable from 'components/DataTable';
import { DestructiveDialog } from 'components/Dialog';
import Card from 'components/card/Card';
import { MdDelete } from 'react-icons/md';
import moment from 'moment';
import { useEffect, useState } from 'react';
import AddUserModal from '../AddUserModal';
const columnHelper = createColumnHelper();

function CustomerUsers({ customerId }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const toast = useToast();

  useEffect(() => {
    const init = async () => {
      await loadUsers();
    };

    init();
  }, []);

  const loadUsers = async () => {
    try {
      const _users = await getCustomerUsersApi(customerId);
      setUsers(_users);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customer users',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteCustomerUserApi(customerId, userToDelete.id);
      await loadUsers();
      toast({
        title: 'Success',
        description: 'User deleted successfully',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        status: 'error',
      });
    }
  };

  const columns = [
    columnHelper.accessor('email', {
      cell: (info) => info.getValue(),
      header: 'Email',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) =>
        info.getValue() ? moment(info.getValue()).format('LLL') : ' - ',
      header: 'Created At',
    }),
    columnHelper.accessor('lastLoginAt', {
      cell: (info) =>
        info.getValue() ? moment(info.getValue()).format('LLL') : ' - ',
      header: 'Last Login',
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <IconButton
          aria-label="Delete user"
          icon={<MdDelete />}
          variant={'ghost'}
          colorScheme={'red'}
          size={'sm'}
          onClick={() => setUserToDelete(info.row.original)}
        />
      ),
    }),
  ];

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <Box minW={'container.lg'} maxW={'max'}>
      <Card>
        <Heading variant={'sectionHeader'} borderColor={'secondaryGray.600'} mb={5}>
          Users
        </Heading>
        <DataTable data={users} columns={columns} />
        <Flex justify={'flex-end'} align={'center'} mt={20}>
          <Button variant={'brand'} onClick={() => setShowAddUserModal(true)}>Add User</Button>
        </Flex>
      </Card>
      <AddUserModal
        show={showAddUserModal}
        header={'Add New User'}
        selectedCustomerId={customerId}
        // companyNames={companyNames}
        onSubmitSuccess={async () => {
          setShowAddUserModal(false);
          await loadUsers();
          toast({
            title: 'Success',
            description: 'User created successfully',
            status: 'success',
          });
        }}
        onModalClose={() => setShowAddUserModal(false)}
      />
      <DestructiveDialog
        show={!!userToDelete}
        header={'Delete User'}
        body={`Are you sure you want to delete ${userToDelete?.email}? This will revoke their access immediately. This action is irreversible.`}
        onDelete={handleDeleteUser}
        onDialogClose={() => setUserToDelete(null)}
      />
    </Box>
  );
}

export default CustomerUsers;
